import { useEffect, useMemo, useState } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import productService from '../../services/productService';
import productTypeService from '../../services/productTypeService';
import brandService from '../../services/brandService';
import fragranceNoteService from '../../services/fragranceNoteService';
import homeSectionService from '../../services/homeSectionService';
import homeSectionDefinitionService from '../../services/homeSectionDefinitionService';
import { getProductImageUrl } from '../../utils/formatters';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import SectionFormModal from './SectionFormModal';

const RULE_TYPE_LABELS = {
  0: { ar: 'يدوي', en: 'Manual' },
  1: { ar: 'براند', en: 'Brand' },
  2: { ar: 'فئة', en: 'Gender' },
  3: { ar: 'نوع منتج', en: 'Product type' },
  4: { ar: 'تصنيف', en: 'Classification' },
  5: { ar: 'نوتة عطر', en: 'Fragrance note' },
  6: { ar: 'خصم', en: 'Discount' },
  7: { ar: 'توصيل مجاني', en: 'Free shipping' },
  8: { ar: 'وصل حديثاً', en: 'New arrivals' },
  9: { ar: 'الأكثر مبيعاً', en: 'Top selling' },
  10: { ar: 'مجموعات', en: 'Gift sets' },
};

const HomeSectionsIndex = () => {
  const { isRtl } = useLocalization();
  const [sections, setSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [fragranceNotes, setFragranceNotes] = useState([]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [sectionItems, setSectionItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);

  const selectedSection = useMemo(
    () => sections.find((s) => s.id === selectedSectionId) || null,
    [sections, selectedSectionId]
  );

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      loadSectionProducts(selectedSection.key);
    } else {
      setSectionItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSectionId]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      const [allSections, allProducts, allBrands, allProductTypes, allNotes] = await Promise.all([
        homeSectionDefinitionService.getAll(),
        productService.getAll(),
        brandService.getAll(),
        productTypeService.getAll(),
        fragranceNoteService.getAll(),
      ]);
      setSections(allSections || []);
      setProducts(allProducts || []);
      setBrands(allBrands || []);
      setProductTypes(allProductTypes || []);
      setFragranceNotes(allNotes || []);
      if ((allSections || []).length > 0) {
        setSelectedSectionId(allSections[0].id);
      }
    } catch (error) {
      console.error('Error loading home sections data:', error);
      setMessage(isRtl ? 'تعذر تحميل البيانات' : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSections = async () => {
    const allSections = await homeSectionDefinitionService.getAll();
    setSections(allSections || []);
    return allSections || [];
  };

  const loadSectionProducts = async (sectionKey) => {
    try {
      setIsSectionLoading(true);
      setMessage('');
      const data = await homeSectionService.getSectionProducts(sectionKey);
      setSectionItems((data || []).map((item) => ({
        ...item,
        id: item.productId,
      })));
    } catch (error) {
      console.error('Error loading section products:', error);
      setMessage(isRtl ? 'تعذر تحميل منتجات القسم' : 'Failed to load section products');
      setSectionItems([]);
    } finally {
      setIsSectionLoading(false);
    }
  };

  const selectedProductIds = useMemo(
    () => new Set(sectionItems.map((item) => item.productId || item.id)),
    [sectionItems]
  );

  const filteredProducts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return products
      .filter((product) => !selectedProductIds.has(product.id))
      .filter((product) => {
        if (!term) return true;
        return (
          product.nameEn?.toLowerCase().includes(term) ||
          product.nameAr?.toLowerCase().includes(term) ||
          product.brandEn?.toLowerCase().includes(term) ||
          product.categoryNameEn?.toLowerCase().includes(term)
        );
      })
      .slice(0, 20);
  }, [products, searchTerm, selectedProductIds]);

  const addProduct = (product) => {
    setMessage('');
    setSectionItems((items) => [
      ...items,
      {
        id: product.id,
        productId: product.id,
        nameEn: product.nameEn,
        nameAr: product.nameAr,
        brandNameEn: product.brandEn,
        brandNameAr: product.brandAr || product.brandEn,
        imageUrl: product.imageUrlFull || product.imageUrl,
      },
    ]);
  };

  const removeProduct = (productId) => {
    setMessage('');
    setSectionItems((items) => items.filter((item) => (item.productId || item.id) !== productId));
  };

  const moveProduct = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sectionItems.length) return;

    setMessage('');
    setSectionItems((items) => {
      const next = [...items];
      const temp = next[index];
      next[index] = next[target];
      next[target] = temp;
      return next;
    });
  };

  const saveSection = async () => {
    if (!selectedSection) return;
    try {
      setIsSaving(true);
      setMessage('');
      await homeSectionService.updateSectionProducts(selectedSection.key, sectionItems);
      await loadSectionProducts(selectedSection.key);
      await refreshSections();
      setMessage(isRtl ? 'تم حفظ ترتيب القسم' : 'Section order saved');
    } catch (error) {
      console.error('Error saving section products:', error);
      setMessage(isRtl ? 'فشل حفظ القسم' : 'Failed to save section');
    } finally {
      setIsSaving(false);
    }
  };

  const clearSection = () => {
    setMessage('');
    setSectionItems([]);
  };

  const moveSectionOrder = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= sections.length) return;

    const next = [...sections];
    const temp = next[index];
    next[index] = next[target];
    next[target] = temp;
    setSections(next);

    try {
      await homeSectionDefinitionService.reorder(
        next.map((s, i) => ({ id: s.id, displayOrder: i + 1 }))
      );
    } catch (error) {
      console.error('Error reordering sections:', error);
      setMessage(isRtl ? 'فشل حفظ الترتيب' : 'Failed to save order');
      refreshSections();
    }
  };

  const toggleSectionActive = async (section) => {
    try {
      await homeSectionDefinitionService.update(section.id, {
        titleAr: section.titleAr,
        titleEn: section.titleEn,
        sectionType: section.sectionType,
        filterValue: section.filterValue,
        autoFillEnabled: section.autoFillEnabled,
        displayLimit: section.displayLimit,
        showOnWeb: section.showOnWeb,
        showOnMobile: section.showOnMobile,
        isActive: !section.isActive,
      });
      await refreshSections();
    } catch (error) {
      console.error('Error toggling section:', error);
      setMessage(isRtl ? 'فشل تحديث القسم' : 'Failed to update section');
    }
  };

  const deleteSection = async (section) => {
    if (section.isSystem) return;
    const confirmText = isRtl ? `هل تريد حذف قسم "${section.titleAr}"؟` : `Delete section "${section.titleEn}"?`;
    if (!window.confirm(confirmText)) return;

    try {
      await homeSectionDefinitionService.remove(section.id);
      if (selectedSectionId === section.id) setSelectedSectionId(null);
      await refreshSections();
    } catch (error) {
      console.error('Error deleting section:', error);
      setMessage(isRtl ? 'فشل حذف القسم' : 'Failed to delete section');
    }
  };

  const openCreateForm = () => {
    setEditingSection(null);
    setFormOpen(true);
  };

  const openEditForm = (section) => {
    setEditingSection(section);
    setFormOpen(true);
  };

  const handleFormSave = async (payload) => {
    if (editingSection) {
      await homeSectionDefinitionService.update(editingSection.id, payload);
      await refreshSections();
    } else {
      const newId = await homeSectionDefinitionService.create(payload);
      const updated = await refreshSections();
      const created = updated.find((s) => s.id === newId);
      if (created) setSelectedSectionId(created.id);
    }
    setFormOpen(false);
  };

  const renderProductThumb = (url, name) => {
    const imageUrl = getProductImageUrl(url);
    return imageUrl ? (
      <img src={imageUrl} alt={name} className="home-section-thumb" />
    ) : (
      <div className="home-section-thumb home-section-thumb-empty" />
    );
  };

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading home sections...'} />;
  }

  return (
    <div className="home-sections-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'إدارة أقسام الصفحة الرئيسية' : 'Home Sections'}</h1>
            <p className="page-description">
              {isRtl
                ? 'أنشئ أقسامك الخاصة، اختر منتجاتها وترتيبها، وحدد مكانها بالصفحة الرئيسية.'
                : 'Create your own sections, curate their products and order, and control their place on the home page.'}
            </p>
          </div>
          <div className="page-header-actions">
            <Button variant="primary" onClick={openCreateForm}>
              {isRtl ? '+ قسم جديد' : '+ New section'}
            </Button>
          </div>
        </div>
      </div>

      {message && <div className="home-section-message">{message}</div>}

      <div className="home-sections-layout">
        <aside className="home-sections-sidebar card">
          <div className="home-section-panel-title">{isRtl ? 'الأقسام' : 'Sections'}</div>
          <div className="home-section-list">
            {sections.length === 0 && (
              <div className="home-section-empty">
                {isRtl ? 'لا توجد أقسام بعد. أنشئ أول قسم.' : 'No sections yet. Create your first one.'}
              </div>
            )}
            {sections.map((section, index) => (
              <div
                key={section.id}
                className={`home-section-row ${selectedSectionId === section.id ? 'active' : ''} ${section.isActive ? '' : 'inactive'}`}
              >
                <div className="home-section-row-order-controls">
                  <button type="button" onClick={() => moveSectionOrder(index, -1)} disabled={index === 0} title={isRtl ? 'لأعلى' : 'Move up'}>↑</button>
                  <button type="button" onClick={() => moveSectionOrder(index, 1)} disabled={index === sections.length - 1} title={isRtl ? 'لأسفل' : 'Move down'}>↓</button>
                </div>
                <button
                  type="button"
                  className="home-section-row-main"
                  onClick={() => setSelectedSectionId(section.id)}
                >
                  <span className="home-section-row-title">{isRtl ? section.titleAr : section.titleEn}</span>
                  <span className="home-section-row-meta">
                    {isRtl ? RULE_TYPE_LABELS[section.sectionType]?.ar : RULE_TYPE_LABELS[section.sectionType]?.en}
                    {' · '}
                    {section.productCount} {isRtl ? 'منتج' : 'products'}
                    {!section.isActive && (isRtl ? ' · معطّل' : ' · disabled')}
                  </span>
                </button>
                <div className="home-section-row-actions">
                  <button type="button" onClick={() => toggleSectionActive(section)} title={isRtl ? 'تفعيل/تعطيل' : 'Toggle active'}>
                    {section.isActive ? '●' : '○'}
                  </button>
                  <button type="button" onClick={() => openEditForm(section)} title={isRtl ? 'تعديل' : 'Edit'}>✎</button>
                  {!section.isSystem && (
                    <button type="button" className="danger" onClick={() => deleteSection(section)} title={isRtl ? 'حذف' : 'Delete'}>×</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </aside>

        <main className="home-sections-main">
          {!selectedSection ? (
            <section className="card home-section-card">
              <div className="home-section-empty">
                {isRtl ? 'اختر قسماً من القائمة أو أنشئ قسماً جديداً.' : 'Pick a section from the list, or create a new one.'}
              </div>
            </section>
          ) : (
            <>
              <section className="card home-section-card">
                <div className="home-section-card-header">
                  <div>
                    <div className="home-section-kicker">{isRtl ? 'الترتيب الحالي' : 'Current order'}</div>
                    <h2>{isRtl ? selectedSection.titleAr : selectedSection.titleEn}</h2>
                    <div className="home-section-rule-note">
                      {selectedSection.autoFillEnabled
                        ? (isRtl
                          ? `يُكمَّل تلقائياً حتى ${selectedSection.displayLimit} منتج حسب القاعدة: ${RULE_TYPE_LABELS[selectedSection.sectionType]?.ar}`
                          : `Auto-fills up to ${selectedSection.displayLimit} products by: ${RULE_TYPE_LABELS[selectedSection.sectionType]?.en}`)
                        : (isRtl ? 'قائمة يدوية بدون تكملة تلقائية' : 'Manual list only, no auto-fill')}
                    </div>
                  </div>
                  <div className="home-section-card-header-actions">
                    <span className="home-section-count">
                      {isRtl ? `${sectionItems.length} منتج` : `${sectionItems.length} products`}
                    </span>
                    <Button variant="secondary" onClick={clearSection} disabled={isSaving || isSectionLoading}>
                      {isRtl ? 'تفريغ القسم' : 'Clear Section'}
                    </Button>
                    <Button variant="primary" onClick={saveSection} disabled={isSaving || isSectionLoading}>
                      {isSaving ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : (isRtl ? 'حفظ الترتيب' : 'Save Order')}
                    </Button>
                  </div>
                </div>

                {isSectionLoading ? (
                  <Loading text={isRtl ? 'جاري تحميل القسم...' : 'Loading section...'} />
                ) : sectionItems.length === 0 ? (
                  <div className="home-section-empty">
                    {selectedSection.autoFillEnabled
                      ? (isRtl
                        ? 'لا توجد منتجات مخصصة. سيستخدم المتجر التكملة التلقائية لهذا القسم.'
                        : 'No custom products selected. The store will use the auto-fill rule for this section.')
                      : (isRtl
                        ? 'لا توجد منتجات مخصصة. هذا القسم لن يظهر بالمتجر حتى تختار منتجات له.'
                        : "No custom products selected. This section won't show in the store until you pick products.")}
                  </div>
                ) : (
                  <div className="home-section-selected-list">
                    {sectionItems.map((item, index) => (
                      <div className="home-section-selected-item" key={item.productId || item.id}>
                        <div className="home-section-order">{index + 1}</div>
                        {renderProductThumb(item.imageUrl, item.nameEn)}
                        <div className="home-section-product-info">
                          <div className="home-section-product-name">{isRtl ? item.nameAr : item.nameEn}</div>
                          <div className="home-section-product-meta">
                            {isRtl ? (item.brandNameAr || item.brandNameEn) : item.brandNameEn}
                          </div>
                        </div>
                        <div className="home-section-row-actions">
                          <button type="button" onClick={() => moveProduct(index, -1)} disabled={index === 0}>↑</button>
                          <button type="button" onClick={() => moveProduct(index, 1)} disabled={index === sectionItems.length - 1}>↓</button>
                          <button type="button" className="danger" onClick={() => removeProduct(item.productId || item.id)}>×</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="card home-section-card">
                <div className="home-section-card-header">
                  <div>
                    <div className="home-section-kicker">{isRtl ? 'إضافة منتجات' : 'Add products'}</div>
                    <h2>{isRtl ? 'كتالوج المنتجات' : 'Product catalog'}</h2>
                  </div>
                  <div className="home-section-search">
                    <input
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder={isRtl ? 'ابحث عن منتج...' : 'Search products...'}
                    />
                  </div>
                </div>

                <div className="home-section-product-grid">
                  {filteredProducts.map((product) => (
                    <button
                      type="button"
                      className="home-section-product-option"
                      key={product.id}
                      onClick={() => addProduct(product)}
                    >
                      {renderProductThumb(product.imageUrlFull || product.imageUrl, product.nameEn)}
                      <span>
                        <strong>{isRtl ? product.nameAr : product.nameEn}</strong>
                        <small>{isRtl ? (product.brandAr || product.brandEn) : product.brandEn}</small>
                      </span>
                      <b>+</b>
                    </button>
                  ))}
                </div>
              </section>
            </>
          )}
        </main>
      </div>

      <SectionFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleFormSave}
        section={editingSection}
        brands={brands}
        productTypes={productTypes}
        fragranceNotes={fragranceNotes}
        isRtl={isRtl}
      />

      <style>{`
        .home-sections-layout {
          display: grid;
          grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
          gap: var(--sp-5);
          align-items: start;
        }

        .home-sections-sidebar {
          padding: var(--sp-4);
          position: sticky;
          top: var(--sp-4);
        }

        .home-section-panel-title,
        .home-section-kicker {
          color: var(--text-muted);
          font-size: var(--fs-xs);
          font-weight: var(--fw-bold);
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-bottom: var(--sp-3);
        }

        .home-section-list {
          display: flex;
          flex-direction: column;
          gap: var(--sp-2);
        }

        .home-section-row {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          align-items: center;
          gap: var(--sp-2);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          background: var(--surface);
          padding: var(--sp-1) var(--sp-2);
        }

        .home-section-row.active {
          border-color: var(--primary);
          background: color-mix(in srgb, var(--primary) 8%, var(--surface));
        }

        .home-section-row.inactive {
          opacity: 0.55;
        }

        .home-section-row-order-controls {
          display: flex;
          flex-direction: column;
        }

        .home-section-row-order-controls button {
          border: none;
          background: transparent;
          color: var(--text-muted);
          cursor: pointer;
          line-height: 1.1;
          font-size: var(--fs-sm);
        }

        .home-section-row-order-controls button:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        .home-section-row-main {
          border: none;
          background: transparent;
          text-align: start;
          cursor: pointer;
          padding: var(--sp-2) 0;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }

        .home-section-row-title {
          font-weight: var(--fw-bold);
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .home-section-row-meta {
          color: var(--text-muted);
          font-size: var(--fs-xs);
        }

        .home-section-row-actions {
          display: flex;
          gap: var(--sp-1);
        }

        .home-section-row-actions button {
          width: 28px;
          height: 28px;
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          background: var(--neutral-50);
          color: var(--text);
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: var(--fw-bold);
        }

        .home-section-row-actions button.danger {
          color: var(--danger);
        }

        .home-sections-main {
          display: grid;
          gap: var(--sp-4);
        }

        .home-section-card {
          padding: var(--sp-4);
          min-width: 0;
        }

        .home-section-card-header {
          display: flex;
          justify-content: space-between;
          gap: var(--sp-4);
          align-items: flex-start;
          margin-bottom: var(--sp-4);
        }

        .home-section-card-header-actions {
          display: flex;
          align-items: center;
          gap: var(--sp-3);
        }

        .home-section-card-header h2 {
          margin: 0;
          font-size: var(--fs-xl);
          color: var(--text);
        }

        .home-section-rule-note {
          color: var(--text-muted);
          font-size: var(--fs-sm);
          margin-top: var(--sp-1);
        }

        .home-section-count,
        .home-section-message {
          border-radius: 999px;
          background: color-mix(in srgb, var(--primary) 10%, var(--surface));
          color: var(--primary);
          font-weight: var(--fw-bold);
          padding: 0.45rem 0.8rem;
          font-size: var(--fs-sm);
        }

        .home-section-message {
          display: inline-flex;
          margin-bottom: var(--sp-4);
        }

        .home-section-empty {
          border: 1px dashed var(--border);
          color: var(--text-muted);
          border-radius: var(--r-lg);
          padding: var(--sp-6);
          text-align: center;
          background: var(--neutral-50);
        }

        .home-section-selected-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: var(--sp-2);
          max-height: 320px;
          min-height: 0;
          overflow-y: auto;
          padding-inline-end: 0.35rem;
        }

        .home-section-selected-item {
          display: grid;
          grid-template-columns: 30px 46px minmax(0, 1fr);
          align-items: center;
          gap: var(--sp-2);
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          background: var(--surface);
          padding: var(--sp-2);
          min-width: 0;
        }

        .home-section-product-option {
          display: grid;
          grid-template-columns: 52px minmax(0, 1fr) auto;
          align-items: center;
          gap: var(--sp-3);
          border: 1px solid var(--border);
          border-radius: var(--r-lg);
          background: var(--surface);
          padding: var(--sp-3);
        }

        .home-section-order {
          width: 30px;
          height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: var(--neutral-100);
          color: var(--text-muted);
          font-weight: var(--fw-bold);
        }

        .home-section-thumb {
          width: 52px;
          height: 52px;
          object-fit: cover;
          border-radius: var(--r-md);
          background: var(--neutral-100);
        }

        .home-section-selected-item .home-section-thumb {
          width: 46px;
          height: 46px;
        }

        .home-section-thumb-empty {
          border: 1px dashed var(--border);
        }

        .home-section-product-name {
          font-weight: var(--fw-bold);
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .home-section-product-meta,
        .home-section-product-option small {
          color: var(--text-muted);
          font-size: var(--fs-sm);
        }

        .home-section-selected-item .home-section-row-actions {
          grid-column: 1 / -1;
          justify-content: flex-end;
          padding-top: 0.1rem;
        }

        .home-section-selected-item .home-section-row-actions button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .home-section-search input {
          width: 100%;
          min-width: 0;
          border: 1px solid var(--border);
          border-radius: var(--r-md);
          padding: 0.65rem 0.85rem;
          background: var(--surface);
          color: var(--text);
        }

        .home-section-product-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: var(--sp-2);
          max-height: none;
          min-height: 0;
          overflow-y: auto;
          padding-inline-end: 0.35rem;
        }

        .home-section-product-option {
          text-align: start;
          cursor: pointer;
          padding: var(--sp-2);
          border-radius: var(--r-md);
        }

        .home-section-product-option span {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.15rem;
        }

        .home-section-product-option strong {
          color: var(--text);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        @media (max-width: 980px) {
          .home-sections-layout {
            grid-template-columns: 1fr;
          }
          .home-sections-sidebar {
            position: static;
          }
          .home-section-card-header {
            align-items: stretch;
            flex-direction: column;
          }
          .home-section-card-header-actions {
            flex-wrap: wrap;
          }
          .home-section-search input {
            width: 100%;
            min-width: 0;
          }
          .home-section-product-grid {
            grid-template-columns: 1fr;
          }
          .home-section-selected-list {
            max-height: 360px;
          }
        }

        @media (max-width: 560px) {
          .home-section-selected-item {
            grid-template-columns: auto 44px minmax(0, 1fr);
          }
          .home-section-thumb {
            width: 44px;
            height: 44px;
          }
        }
      `}</style>
    </div>
  );
};

export default HomeSectionsIndex;
